import {
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@atrvd/saas-ui'
import type { Pack } from '@/types/banking'

/**
 * Represents the active filter state for the Facilities table.
 *
 * @property search  - Free-text query matched against the Display Name and the Legal Name.
 * @property status  - Active/inactive toggle; 'all' disables the constraint.
 * @property packId  - Pack assignment filter. 'all' disables it; 'none' matches
 *                     facilities where pack_id IS NULL (not assigned to any pack);
 *                     any other value is a specific pack id (DCP-171 named packs).
 */
export interface FacilitiesFilterState {
  search: string
  status: 'all' | 'active' | 'inactive'
  packId: 'all' | 'none' | string
}

interface FacilitiesFiltersProps {
  /** Current filter values — component is fully controlled (stateless). */
  filters: FacilitiesFilterState
  /** Called with the complete next filter state on any control change. */
  onFiltersChange: (filters: FacilitiesFilterState) => void
  /** Packs available for the org, used to build the pack dropdown options. */
  packs: Pack[]
}

/**
 * Stateless filter bar for the Facilities settings table.
 *
 * Renders three controls side-by-side: a name search input, a status
 * dropdown, and a pack dropdown (DCP-171 named packs). The parent owns the
 * state and receives the full updated filter object on every change.
 *
 * Follows the same shape as BalanceImportFilters: a flex-wrap container
 * with gap-3 spacing and `@atrvd/saas-ui` Select primitives.
 */
export function FacilitiesFilters({ filters, onFiltersChange, packs }: FacilitiesFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Name search — onChange receives the string value directly (no Event wrapper) */}
      <SearchInput
        placeholder="Search by name..."
        value={filters.search}
        onChange={(value) => onFiltersChange({ ...filters, search: value })}
        clearable
        className="w-64"
      />

      {/* Status filter */}
      <Select
        value={filters.status}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            status: value as FacilitiesFilterState['status'],
          })
        }
      >
        {/* aria-label supplies the accessible name for screen readers —
            these filter dropdowns have no associated visible <label>. */}
        <SelectTrigger className="w-40" aria-label="Filter by status">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      {/* Pack filter — 'none' sentinel maps to pack_id IS NULL at the predicate
          level; any other value is a specific pack id. */}
      <Select
        value={filters.packId}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            packId: value as FacilitiesFilterState['packId'],
          })
        }
      >
        <SelectTrigger className="w-40" aria-label="Filter by pack">
          <SelectValue placeholder="Pack" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Packs</SelectItem>
          {packs.map((pack) => (
            <SelectItem key={pack.id} value={pack.id}>
              {pack.name}
            </SelectItem>
          ))}
          <SelectItem value="none">No Pack</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
